// Captain Skill System (design doc 16.19/16.31): a small, trackable bonus
// reflecting how a captain spends their time -- NOT a power-progression
// system. Skill never gates hardware access and never substitutes for it.

export type SkillName =
  | "Mining"
  | "Combat"
  | "Scanning"
  | "Repair"
  | "Piloting"
  | "TractorBeam"
  | "Command";

export const SKILL_NAMES: SkillName[] = [
  "Mining",
  "Combat",
  "Scanning",
  "Repair",
  "Piloting",
  "TractorBeam",
  "Command"
];

export const MAX_LEVEL = 10;
export const BONUS_PERCENT_PER_LEVEL = 1; // +1%/level, up to +10% at max (16.19)
export const XP_PER_SECOND_ENGAGED = 1; // 1 XP per second of active engagement (16.31)

// XP required to go from level N-1 to level N is `N x 600` -- linear, not
// flat (16.31). Cumulative XP needed to REACH level N (0 = untrained):
// `600 x (1+2+...+N)` = `600 x N x (N+1) / 2`. Level 10 (max) = 33,000 XP.
export function xpForLevel(level: number): number {
  return (600 * level * (level + 1)) / 2;
}

// Current level (0-10) for a total accumulated XP amount.
export function levelFromXp(xp: number): number {
  let level = 0;
  while (level < MAX_LEVEL && xp >= xpForLevel(level + 1)) {
    level++;
  }
  return level;
}

export function xpToNextLevel(xp: number): number | null {
  const level = levelFromXp(xp);
  if (level >= MAX_LEVEL) {
    return null;
  }
  return xpForLevel(level + 1) - xp;
}

export function bonusPercent(xp: number): number {
  return levelFromXp(xp) * BONUS_PERCENT_PER_LEVEL;
}

export function bonusMultiplier(xp: number): number {
  return 1 + bonusPercent(xp) / 100;
}

// 1 XP per second of active engagement in that skill's relevant activity
// (mining while the laser is firing, combat while a weapon/drone is
// engaged, piloting while traveling between systems, etc.) -- deliberately
// not per-kill or per-m3 (16.31).
export function addEngagementSeconds(currentXp: number, seconds: number): number {
  return currentXp + seconds * XP_PER_SECOND_ENGAGED;
}

// Applies a skill's flat +1%/level bonus to a base stat -- the same shape
// for every skill except Piloting, which stacks differently (16.31: ship
// speed multiplies role/size multipliers together with `1 + skill_bonus`
// rather than scaling a single base number -- see shipSpeed.ts).
export function applySkillBonus(baseValue: number, xp: number): number {
  return baseValue * bonusMultiplier(xp);
}

// Maximum simultaneous fleet escorts, governed by Command level (16.69).
//
// 16.19 originally gated fleet access on installing a **Command Module**,
// which 16.16's fixed roster removed the possibility of -- leaving Command
// as the one skill with nothing to boost. This replaces that gate with the
// skill itself, needing no fitting system: capacity is what scales, not
// access.
//
// **Base is 1, deliberately not 0.** Command XP only accrues while a fleet
// ship is actually engaging (see expeditionScreen's accrueSkillXp), so a
// zero-capacity floor would be unescapable -- no fleet means no Command XP
// means no fleet, forever. Starting at 1 keeps the skill earnable.
export const BASE_FLEET_CAPACITY = 1;
export const FLEET_CAPACITY_LEVELS_PER_SLOT = 2; // +1 escort per 2 Command levels -> 6 at max

export function maxFleetSize(commandXp: number): number {
  return BASE_FLEET_CAPACITY + Math.floor(levelFromXp(commandXp) / FLEET_CAPACITY_LEVELS_PER_SLOT);
}

export type CaptainSkillXp = Record<SkillName, number>;

export function createCaptainSkills(): CaptainSkillXp {
  return {
    Mining: 0,
    Combat: 0,
    Scanning: 0,
    Repair: 0,
    Piloting: 0,
    TractorBeam: 0,
    Command: 0
  };
}
